import type { Attachment } from '@medplum/fhirtypes'
import { type JSX, useCallback, useRef, useState } from 'react'
import { isExternalUrl } from '#src/components/attachments/attachment-utils.ts'
import { AttachmentExternalLinkDisplay } from '#src/components/attachments/AttachmentExternalLinkDisplay.tsx'
import { AttachmentFallbackDisplay } from '#src/components/attachments/AttachmentFallbackDisplay.tsx'
import { AttachmentImageDisplay } from '#src/components/attachments/AttachmentImageDisplay.tsx'
import { AttachmentPDFDisplay } from '#src/components/attachments/AttachmentPDFDisplay.tsx'
import { AttachmentVideoDisplay } from '#src/components/attachments/AttachmentVideoDisplay.tsx'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

export interface AttachmentDisplayProps {
  readonly value?: Attachment
  readonly maxWidth?: number
  className?: string
  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  onTitleChange?: (title: string) => void
  /**
   * Parent DocumentReference id; required to surface the retry-conversion
   * button on the failed-state placeholder. Omit for contexts where the
   * attachment is not backed by a single DocumentReference (e.g. ad-hoc
   * external links) — the retry affordance silently won't render.
   */
  documentReferenceId?: string
}

export function AttachmentDisplay(
  props: AttachmentDisplayProps
): JSX.Element | null {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false)
  const { contentType } = props.value ?? {}
  const url = props.value?.url
  const isUrlExternal = isExternalUrl(url)
  const { onExpandedChange } = props
  const isExpandedControlled = props.isExpanded !== undefined

  const stableUrl = useStableAttachmentUrl(url)

  const prevStableUrlRef = useRef(stableUrl)
  if (prevStableUrlRef.current !== stableUrl) {
    prevStableUrlRef.current = stableUrl
    if (!isExpandedControlled) {
      setUncontrolledExpanded(false)
    }
  }

  const isExpanded = props.isExpanded ?? uncontrolledExpanded
  const handleExpandedChange = useCallback(
    (expanded: boolean): void => {
      if (!isExpandedControlled) {
        setUncontrolledExpanded(expanded)
      }
      onExpandedChange?.(expanded)
    },
    [isExpandedControlled, onExpandedChange]
  )

  if (!props.value) {
    return null
  }

  if (isUrlExternal) {
    return (
      <AttachmentExternalLinkDisplay
        attachment={props.value}
        onTitleChange={props.onTitleChange}
      />
    )
  }

  if (!stableUrl) {
    return null
  }

  if (contentType === 'application/pdf') {
    return (
      <AttachmentPDFDisplay
        attachment={props.value}
        url={stableUrl}
        isExpanded={isExpanded}
        onExpandedChange={handleExpandedChange}
        onTitleChange={props.onTitleChange}
      />
    )
  }

  if (contentType?.startsWith('image/')) {
    return (
      <AttachmentImageDisplay
        attachment={props.value}
        url={stableUrl}
        isExpanded={isExpanded}
        onExpandedChange={handleExpandedChange}
        onTitleChange={props.onTitleChange}
        documentReferenceId={props.documentReferenceId}
      />
    )
  }

  if (contentType?.startsWith('video/')) {
    return (
      <AttachmentVideoDisplay
        attachment={props.value}
        url={stableUrl}
        onTitleChange={props.onTitleChange}
        documentReferenceId={props.documentReferenceId}
      />
    )
  }

  return (
    <AttachmentFallbackDisplay
      attachment={props.value}
      onTitleChange={props.onTitleChange}
    />
  )
}
